import { usePrefetchedSuspenseQueries, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useReviewCardFsrsResolutionQueryOptions } from "@-/dev.improve.haniverse.lib.cards.study-card-data/use-review-card-fsrs-resolution-query";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import {
  selectWordContextSentence,
  useWordCharacterBacksQueryOptions,
  useWordContextSentencesQueryOptions,
  type WordCharacterBack,
  type WordContextSentence,
  type WordContextSentenceCandidate,
} from "@-/dev.improve.haniverse.lib.sentence.context";
import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import {
  DrillPageContextProvider,
  WordDrillContextProvider,
  type WordDrillType,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useDrillPageSentenceDrill } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.sentence-drill";
import { buildWordDrillExpectedAnswerSet } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.submit";
import { useMemo, useState, type ReactNode } from "react";

/** A word card has no reveal audio: its answer is its characters, not one glyph's readings. */
function playNoRevealAudio() {}

/** A word drill's providers, below its gate: the frozen sentence is non-null by construction. */
function WordDrillProviders({
  character,
  characterBacks,
  children,
  drillType,
  morphemeHrefObject,
  sentence,
}: {
  character: string;
  characterBacks: readonly WordCharacterBack[];
  children: ReactNode;
  drillType: WordDrillType;
  morphemeHrefObject: MorphemeHrefObject;
  sentence: WordContextSentence;
}) {
  const sentenceDrill = useDrillPageSentenceDrill({
    drillTypeKind: drillType.kind,
    sentence: sentence.sentence,
  });
  const expectedAnswerSet = useMemo(
    () => buildWordDrillExpectedAnswerSet({ characterBacks, drillType }),
    [characterBacks, drillType],
  );

  return (
    <WordDrillContextProvider
      characterBacks={characterBacks}
      drillType={drillType}
      sentence={sentence}
    >
      <DrillPageContextProvider
        character={character}
        drillType={drillType}
        expectedAnswerSet={expectedAnswerSet}
        morphemeHrefObject={morphemeHrefObject}
        playRevealAudio={playNoRevealAudio}
        sentenceDrill={sentenceDrill}
      >
        {children}
      </DrillPageContextProvider>
    </WordDrillContextProvider>
  );
}

/**
 * The gate for a WORD drill. A word card has no grapheme slices by construction — `morpheme` rows
 * are per-glyph, and a multi-character word is not one — so it needs only its characters' backs
 * (the answer it is graded against and the tiles its back renders) and the sentence that presents
 * it. The sentence is frozen below for the mounted presentation; with none, it renders
 * `MorphemeNotFound`.
 */
export function WordDrillGateSuspense({
  character,
  children,
  contextSentence,
  drillType,
  morphemeHrefObject,
}: {
  character: string;
  children: ReactNode;
  contextSentence: string | undefined;
  drillType: WordDrillType;
  morphemeHrefObject: MorphemeHrefObject;
}) {
  const wordCharacterBacksQueryOptions = useWordCharacterBacksQueryOptions(character);
  const wordContextSentencesQueryOptions = useWordContextSentencesQueryOptions(character);
  const [{ data: characterBacks }, { data: sentences }] = usePrefetchedSuspenseQueries({
    queries: [wordCharacterBacksQueryOptions, wordContextSentencesQueryOptions],
  });

  // Missing offline content belongs to a downloadable level pack. The query determines this
  // from the same source it read, including its dictionary-prompt fallback.
  if (sentences.kind === "not-in-offline-data") {
    return (
      <MorphemeNotFound
        description="This word isn't in the downloaded packs. Download its level pack in Settings → Offline Packs, or reconnect to review it."
        morpheme={character}
      />
    );
  }

  return (
    <WordDrillSentenceGate
      character={character}
      characterBacks={characterBacks}
      contextSentence={contextSentence}
      drillType={drillType}
      morphemeHrefObject={morphemeHrefObject}
      sentenceCandidates={sentences.candidates}
    >
      {children}
    </WordDrillSentenceGate>
  );
}

/** Freezes the word's sentence for the mounted presentation; with none, `MorphemeNotFound`. */
function WordDrillSentenceGate({
  character,
  characterBacks,
  children,
  contextSentence,
  drillType,
  morphemeHrefObject,
  sentenceCandidates,
}: {
  character: string;
  characterBacks: readonly WordCharacterBack[];
  children: ReactNode;
  contextSentence: string | undefined;
  drillType: WordDrillType;
  morphemeHrefObject: MorphemeHrefObject;
  sentenceCandidates: readonly WordContextSentenceCandidate[];
}) {
  // `fsrs_card.reps` is the card's review counter — it advances the sentence rotation, so a word
  // shows a different example each time it comes up. Read with a plain (non-suspending) query so
  // a card with no FSRS row yet still renders: it just starts at rotation 0.
  const fsrsResolutionQueryOptions = useReviewCardFsrsResolutionQueryOptions(character, drillType);
  const { data: fsrsResolution } = useQuery(fsrsResolutionQueryOptions);
  const reviewCount = fsrsResolution?.fsrsCard?.reps ?? 0;

  // Freeze the prompt for this mounted presentation. Neither a reveal, a query refetch nor a
  // released/recreated face may change the sentence halfway through an answer. Prefer the exact
  // sentence selected during queue preparation when it is available in the current candidates.
  const [sentence] = useState(() => {
    // Matched by the sentence's text, which is stable across static rebuilds and data sources.
    const forwarded = sentenceCandidates.find(
      (candidate) => candidate.sentence === contextSentence,
    );
    if (forwarded?.translation) {
      return { ...forwarded, translation: forwarded.translation };
    }
    return selectWordContextSentence({
      candidates: sentenceCandidates,
      reviewCount,
      userCardId: character,
    });
  });

  if (sentence === null) {
    // The corpus genuinely has no translated sentence for this word, so there is nothing to show.
    return <MorphemeNotFound morpheme={character} />;
  }

  return (
    <WordDrillProviders
      character={character}
      characterBacks={characterBacks}
      drillType={drillType}
      morphemeHrefObject={morphemeHrefObject}
      sentence={sentence}
    >
      {children}
    </WordDrillProviders>
  );
}
